// ZONA EDIFICABILĂ pe harta din «Localizare & temă» — unde se poate construi,
// după ce laturile își iau retragerile.
//
// De ce pe ecranul ăsta: acolo se scriu retragerile, și tot acolo se vede
// întâi ce rămâne din lot după ele. Cifra „4,00 m la vecini" nu spune nimic
// singură; pe un lot îngust de 38 m ea mănâncă un sfert din lățime, pe unul de
// 120 m nu se simte. Hașura o arată înainte de generare, nu după.
//
// ACEEAȘI DEFINIȚIE CA A MOTORULUI, altfel desenul minte. Motorul nu retrage
// laturile ca drepte, ci scade din lot banda fiecărei laturi:
// `geometry.erode_per_side` face `lot − reuniunea(segment.buffer(d))`, cu
// capete rotunde, și își scrie chiar el motivul — „«cel puțin d de la segment»
// e literalmente un buffer al segmentului; asta dă și racordarea corectă în
// colțul reintrant, pe care intersecția de semiplane n-o poate da". Aici e
// aceeași operație, cu aceeași bibliotecă de tăiat poligoane pe care o
// folosește deja editorul de tronson.
//
// CINE E LA STRADĂ se decide exact ca la trimiterea către motor
// (`control-plane/services/siteplan.py::_laturi`): o latură e vecin până se
// dovedește altfel, e stradă dacă e declarată așa în acte, și e stradă dacă
// omul a marcat o poartă pe ea — acolo unde a pus intrarea există o stradă,
// altminteri n-ar fi pus-o. Dacă regula de aici s-ar despărți de cea de acolo,
// omul ar vedea hașurată o zonă pe care generarea n-o folosește.
import { laWgs84 } from "../viewer/plan/stereo70";
import { t } from "../i18n";
import type { MultiPolygon, Polygon, Ring } from "polygon-clipping";
import { taie } from "../viewer/taietor";
import type { Latura, ZonaAcces } from "./dosarclient";
import { liniiHasuraMulti } from "../viewer/plan/hasura";

/** Un punct de contur: [lat, lon], ca peste tot pe ecranul ăsta. */
export type Pct = [number, number];

export type Fel = "strada" | "vecin";

/** Segmente pe sfertul de cerc de la capătul unei benzi — `quad_segs=8`, cât
 *  are și motorul. Mai puține s-ar vedea ca un colț tăiat. */
const SFERT = 8;

/** Sub atât, retragerea nu taie nimic și banda ar fi o linie. */
const EPS_M = 1e-6;

/** Metri pe grad, în jurul unui contur. Aceeași aproximare echidreptunghiulară
 *  ca `Harta.arie` și din același motiv: pe câteva sute de metri eroarea e sub
 *  o miime, iar cifra exactă o recalculează oricum serverul, în Stereo 70. */
function reper(pts: Pct[]): { mx: number; my: number } {
  const lat0 = (pts.reduce((s, p) => s + p[0], 0) / pts.length) * Math.PI / 180;
  return {
    mx: 111320 * Math.cos(lat0),
    my: 111132.92 - 559.82 * Math.cos(2 * lat0) + 1.175 * Math.cos(4 * lat0),
  };
}

/** Indicele laturii pe care stă un punct: cea de care e cel mai aproape.
 *
 *  Punct-segment, nu punct-vârf, și într-un reper în metri — o poartă pusă la
 *  mijlocul unei laturi lungi e mai aproape de vârfurile laturii scurte de
 *  lângă ea, iar în grade un grad de longitudine e cu vreo 30 % mai scurt
 *  decât unul de latitudine. Amândouă greșelile ar muta poarta pe altă latură
 *  decât cea de sub ea. */
export function laturaDeSub(contur: Pct[], p: Pct): number | null {
  if (contur.length < 3) return null;
  const { mx, my } = reper(contur);
  let bun: number | null = null, dist = Infinity;
  for (let i = 0; i < contur.length; i++) {
    const a = contur[i], b = contur[(i + 1) % contur.length];
    const ax = a[1] * mx, ay = a[0] * my;
    const bx = b[1] * mx, by = b[0] * my;
    const px = p[1] * mx, py = p[0] * my;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0
      : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const d = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    if (d < dist) { dist = d; bun = i; }
  }
  return bun;
}

/** Mijlocul unei zone de acces, în grade. */
export function mijloculZonei(z: ZonaAcces): Pct {
  return [(z.p0_wgs84[0] + z.p1_wgs84[0]) / 2, (z.p0_wgs84[1] + z.p1_wgs84[1]) / 2];
}

/** Abaterea unghiulară maximă între laturi consecutive ca să fie «aceeași
 *  latură logică». Un front la stradă vine de pe planul topo rupt în două-trei
 *  laturi aproape coliniare (pe Bd. Timișoara 82: 29,7 m + 24,4 m), iar un lot
 *  din cadastru are colțuri-ciorchine de câțiva centimetri. */
export const COLINIAR_DEG = 15;

/** Laturile de la `i0` la `i1` (pe arcul scurt al conturului) dacă formează o
 *  singură latură LOGICĂ — toate cu direcția la cel mult `COLINIAR_DEG` de
 *  direcția coardei dintre capete —, altfel `null`.
 *
 *  E ce face «două clicuri pe aceeași latură» să meargă și pe un contur de
 *  topo cu 36 de vârfuri: omul vede o latură dreaptă, planșa o are din trei
 *  bucăți. Zona rămâne un segment drept între cele două capete; pe un front
 *  aproape drept abaterea față de contur e de centimetri. */
export function laturaLogica(contur: Pct[], i0: number, i1: number,
                             prag = COLINIAR_DEG): number[] | null {
  const n = contur.length;
  if (n < 3 || i0 < 0 || i1 < 0 || i0 >= n || i1 >= n) return null;
  if (i0 === i1) return [i0];
  const { mx, my } = reper(contur);
  const xy = (p: Pct) => [p[1] * mx, p[0] * my] as const;
  const unghi = (a: Pct, b: Pct) => {
    const [ax, ay] = xy(a), [bx, by] = xy(b);
    return Math.atan2(by - ay, bx - ax);
  };
  const abatere = (u: number, v: number) => {
    let d = Math.abs(u - v) % (2 * Math.PI);
    if (d > Math.PI) d = 2 * Math.PI - d;
    return (d * 180) / Math.PI;
  };
  // Arcul scurt: înainte (i0 → i1) sau înapoi (i1 → i0), în ordinea conturului.
  const inainte = (i1 - i0 + n) % n, inapoi = (i0 - i1 + n) % n;
  const start = inainte <= inapoi ? i0 : i1;
  const pasi = Math.min(inainte, inapoi);
  const laturi: number[] = [];
  for (let k = 0; k <= pasi; k++) laturi.push((start + k) % n);
  const coarda = unghi(contur[laturi[0]], contur[(laturi[laturi.length - 1] + 1) % n]);
  for (const i of laturi) {
    const a = contur[i], b = contur[(i + 1) % n];
    if (a[0] === b[0] && a[1] === b[1]) continue;    // vârf dublat, nu latură
    if (abatere(unghi(a, b), coarda) > prag) return null;
  }
  return laturi;
}

/** Felul fiecărei laturi a conturului. V. antetul: vecin până la proba
 *  contrarie, stradă dacă o spun actele sau dacă are pe ea o zonă de acces.
 *  Zona se leagă de latură prin mijlocul ei, ca în
 *  `control-plane/services/siteplan.py::_zone`. */
export function felurileLaturilor(contur: Pct[], laturi: Latura[],
                                  zone: ZonaAcces[]): Fel[] {
  const fel: Fel[] = contur.map((_, i) =>
    laturi[i]?.tip === "strada" ? "strada" : "vecin");
  for (const z of zone) {
    if (!z.p0_wgs84 || !z.p1_wgs84) continue;
    const i = laturaDeSub(contur, mijloculZonei(z));
    if (i !== null) fel[i] = "strada";
  }
  return fel;
}

/** Cât se prelungește banda dincolo de un vârf CONVEX: destul cât să iasă
 *  din lot pe orice parcelă reală. */
const PRELUNGIRE_M = 2000;
/** Sub acest sinus al unghiului dintre două laturi, vârful e coliniar: un
 *  capăt de porțiune, nu un colț. */
const COLINIAR_SIN = 1e-3;

type Capat = "prelungit" | "drept" | "rotund";

/** Banda de lângă o latură: tot ce e mai aproape de `d` de segmentul ei.
 *
 *  Dreptunghiul laturii, cu fiecare capăt după felul vârfului de acolo
 *  (`capA`, `capB`):
 *  - «prelungit» la un vârf convex: banda se PRELUNGEȘTE dincolo de vârf,
 *    drept — adică retragerea se măsoară față de LINIA laturii, cum o
 *    desenează un arhitect: cele două linii de retragere se taie într-un colț
 *    ascuțit. Capătul rotund de până acum (buffer-ul segmentului) lăsa, când
 *    laturile vecine au retrageri diferite, o «ureche» în sfert de cerc cu
 *    raza retragerii mari peste banda celei mici — pe Ghencea 91D (29.10.2026)
 *    colțurile ieșeau cu retrageri pe care actul n-o cere nicăieri. Cu
 *    retrageri egale cele două desene coincid.
 *  - «drept» la un vârf coliniar (capătul unei porțiuni): banda se termină
 *    exact în vârf, cu o treaptă — prelungită, ar lua toată latura.
 *  - «rotund» la un vârf concav (intrând): acolo linia laturii ar tăia în lot
 *    dincolo de vârf; distanța față de limită e distanța față de VÂRF, deci un
 *    arc — racordarea pe care o dă și motorul (`geometry.erode_per_side`). */
function banda(ax: number, ay: number, bx: number, by: number, d: number,
               capA: Capat = "rotund", capB: Capat = "rotund",
               extA: number = PRELUNGIRE_M, extB: number = PRELUNGIRE_M): Ring {
  const ux = bx - ax, uy = by - ay;
  const len = Math.hypot(ux, uy);
  if (len < EPS_M) return [];
  const nx = -uy / len * d, ny = ux / len * d;
  const tx = ux / len, ty = uy / len;
  const inel: number[][] = [];
  if (capB === "rotund") {
    // Capătul dinspre b, rotit din latura dreaptă spre cea stângă.
    for (let k = 0; k <= 2 * SFERT; k++) {
      const t = (Math.PI * k) / (2 * SFERT);
      const co = Math.cos(t), si = Math.sin(t);
      inel.push([bx + nx * co + tx * d * si, by + ny * co + ty * d * si]);
    }
  } else {
    const e = capB === "prelungit" ? extB : 0;
    inel.push([bx + nx + tx * e, by + ny + ty * e]);
    inel.push([bx - nx + tx * e, by - ny + ty * e]);
  }
  if (capA === "rotund") {
    for (let k = 0; k <= 2 * SFERT; k++) {
      const t = (Math.PI * k) / (2 * SFERT);
      const co = Math.cos(t), si = Math.sin(t);
      inel.push([ax - nx * co - tx * d * si, ay - ny * co - ty * d * si]);
    }
  } else {
    const e = capA === "prelungit" ? extA : 0;
    inel.push([ax - nx - tx * e, ay - ny - ty * e]);
    inel.push([ax + nx - tx * e, ay + ny - ty * e]);
  }
  inel.push(inel[0]);
  return inel as Ring;
}

/** CÂT se prelungește banda laturii `i` dincolo de vârful `v` (convex), ca să
 *  se taie exact cu banda laturii vecine `j`: liniile de retragere ale celor
 *  două, la `dI` și `dJ` de laturi, se întâlnesc la `(dJ − dI·cos θ) / sin θ`
 *  de vârf, măsurat de-a lungul laturii `i`, unde θ e cât cotește conturul în
 *  vârf. Atât, și un metru în plus ca tăierea să nu lase o așchie.
 *
 *  Prelungirea fixă de 2 km (`PRELUNGIRE_M`) era bună pe colțuri drepte, dar
 *  pe un FRONT CURB — frontul la Șos. Pipera-Tunari e un arc din 11 laturi de
 *  2–14 m — fiecare coardă prelungită cu 2 km tăia în diagonală prin lot, și
 *  edificabilul ieșea cu un colț mâncat mult peste cei 16,5 m ceruți
 *  (Oana, 30.10.2026). Cu `dJ = 0` (vecina fără retragere) iese ≤ 0, adică
 *  banda se oprește în vârf — acolo oricum nu cere nimeni retragere. */
function prelungirea(xy: number[][], laVarf: number, dI: number, dJ: number): number {
  const n = xy.length;
  const p = xy[laVarf];
  const a = xy[(laVarf - 1 + n) % n], b = xy[(laVarf + 1) % n];
  const u = [p[0] - a[0], p[1] - a[1]], v = [b[0] - p[0], b[1] - p[1]];
  const lu = Math.hypot(u[0], u[1]) || 1, lv = Math.hypot(v[0], v[1]) || 1;
  const cos = (u[0] * v[0] + u[1] * v[1]) / (lu * lv);
  const sin = Math.abs(u[0] * v[1] - u[1] * v[0]) / (lu * lv);
  if (sin < COLINIAR_SIN) return PRELUNGIRE_M;
  // Aceeași formulă pentru latura care intră și pentru cea care iese din
  // vârf: unghiul e același, iar rolurile lui dI și dJ se schimbă între ele.
  const t = (dJ - dI * cos) / sin;
  return Math.max(0, Math.min(PRELUNGIRE_M, t + 1));
}


/** Felul capătului benzii la fiecare vârf al conturului (în metri, `xy`):
 *  «rotund» la vârfurile concave, «prelungit» la cele convexe, «drept» la
 *  cele coliniare. */
function capeteleVarfurilor(xy: number[][]): Capat[] {
  const n = xy.length;
  let arie2 = 0;
  for (let i = 0; i < n; i++) {
    const a = xy[i], b = xy[(i + 1) % n];
    arie2 += a[0] * b[1] - b[0] * a[1];
  }
  const sens = arie2 >= 0 ? 1 : -1;
  return xy.map((p, i) => {
    const a = xy[(i - 1 + n) % n], b = xy[(i + 1) % n];
    const cruce = (p[0] - a[0]) * (b[1] - p[1]) - (p[1] - a[1]) * (b[0] - p[0]);
    const sinus = cruce / (Math.hypot(p[0] - a[0], p[1] - a[1]) * Math.hypot(b[0] - p[0], b[1] - p[1]) || 1);
    if (Math.abs(sinus) < COLINIAR_SIN) return "drept";
    return sinus * sens < 0 ? "rotund" : "prelungit";
  });
}

/** Ce rămâne construibil din lot după retrageri, ca multipoligon în [lat, lon].
 *
 *  `retrageri[i]` e retragerea laturii i, în metri. Zero sau lipsă înseamnă
 *  latură care nu taie nimic — nu „construiește pe limită din greșeală", ci
 *  pur și simplu o bandă de lățime zero.
 *
 *  Poate întoarce mai multe bucăți, sau niciuna: pe un lot îngust cu retrageri
 *  mari zona edificabilă chiar dispare, iar asta e informația cea mai utilă pe
 *  care o poate da ecranul. */
/** Conturul, mutat odată cu pinul — în amândouă reperele.
 *
 *  CÂND. Numai pentru un contur a cărui POZIȚIE e aproximativă: el a fost așezat
 *  cu centrul pe adresa geocodată, deci punctul și forma sunt același lucru, iar
 *  mutarea pinului înseamnă „nu acolo, ci aici". Un contur venit dintr-o
 *  ridicare topografică în Stereo 70 nu se atinge: acolo poziția e măsurată, iar
 *  a o târî după un pin ar strica singura dată exactă din dosar.
 *
 *  DE CE AMÂNDOUĂ REPERELE. `control-plane/services/siteplan.py` citește
 *  `contur_stereo70 or contur_wgs84` — în ordinea asta. Mutat doar în grade,
 *  conturul s-ar vedea mutat pe hartă și ar pleca nemutat la motor, ceea ce e
 *  mai rău decât să nu se miște deloc: ecranul ar minți.
 *
 *  Scara e cea echidreptunghiulară a lui `reper`, ca peste tot în ecranul ăsta:
 *  pe o mutare de câteva sute de metri eroarea e sub o miime, iar poziția e
 *  oricum declarată aproximativă. Forma nu se deformează — toate vârfurile se
 *  mută cu ACELAȘI vector. */
export function mutaConturul(
  conturWgs: Pct[] | null | undefined,
  conturStereo: Pct[] | null | undefined,
  de: { lat: number; lon: number } | null,
  la: { lat: number; lon: number },
  zone?: ZonaAcces[] | null,
): { contur_wgs84?: Pct[]; contur_stereo70?: Pct[]; zone_acces?: ZonaAcces[] } {
  if (!de || (!conturWgs?.length && !conturStereo?.length)) return {};
  const dLat = la.lat - de.lat, dLon = la.lon - de.lon;
  if (dLat === 0 && dLon === 0) return {};
  const out: { contur_wgs84?: Pct[]; contur_stereo70?: Pct[];
               zone_acces?: ZonaAcces[] } = {};

  // CÂND EXISTĂ STEREO 70, EL E ADEVĂRUL, iar gradele se RECALCULEAZĂ din el.
  //
  // Prima variantă muta fiecare reper pe socoteala lui: gradele cu (dLat, dLon),
  // metrii cu aproximarea echidreptunghiulară. Sunt două translaţii diferite,
  // iar conturul ajungea în dezacord CU EL ÎNSUŞI — măsurat pe dosarul
  // beneficiarului (22.09.2026): 2,22 m, uniform pe toate vârfurile.
  //
  // Şi asta se vedea ca o eroare de porţi, nu de contur: poarta se lipeşte de
  // conturul în GRADE, pe care îl desenează harta, iar motorul verifică pe cel
  // în METRI. Între ele rămânea exact diferenţa — «punctul este la 2,22 m de
  // conturul terenului», adică fix cifra de mai sus.
  //
  // Transformarea înainte (Stereo -> WGS) e exactă şi o avem; cea inversă nu e
  // în ecran. Deci se mută metrii — acolo trăieşte înţelesul unei mutări — şi
  // se derivă gradele. Aproximarea rămâne doar în MĂRIMEA pasului, iar poziţia
  // e oricum declarată aproximativă.
  const { mx, my } = reper(conturWgs?.length ? conturWgs : [[la.lat, la.lon]]);
  const dN = dLat * my, dE = dLon * mx;

  if (conturStereo?.length) {
    const mutat = conturStereo.map(([n, e]) => [n + dN, e + dE] as Pct);
    out.contur_stereo70 = mutat;
    out.contur_wgs84 = mutat.map(([n, e]) => {
      const p = laWgs84(n, e);
      return [p.lat, p.lon] as Pct;
    });
  } else if (conturWgs?.length) {
    out.contur_wgs84 = conturWgs.map(([a, o]) => [a + dLat, o + dLon] as Pct);
  }

  // ŞI ZONELE DE ACCES, fiindcă ele stau PE contur.
  //
  // O zonă e un segment de pe muchie, nu un obiect lângă ea: adaptorul o
  // lipeşte pe conturul trimis şi o lasă deoparte când s-a desprins (peste 3 m).
  // Conturul tras de sub ele le-ar lăsa în gol la fix atâţia metri cât s-a
  // mutat pinul.
  //
  // Se mută în acelaşi reper ca el: cine are metri se mută în metri şi îşi
  // recalculează gradele, ca să nu se despartă de conturul pe care stă.
  if (zone?.length) {
    const capat = (w: Pct, st: Pct | null | undefined):
        { wgs: Pct; st: Pct | null } => {
      if (!st) return { wgs: [w[0] + dLat, w[1] + dLon], st: null };
      const n = st[0] + dN, e = st[1] + dE;
      const p = laWgs84(n, e);
      return { wgs: [p.lat, p.lon], st: [n, e] };
    };
    out.zone_acces = zone.map((z) => {
      const a = capat(z.p0_wgs84, z.p0_stereo70), b = capat(z.p1_wgs84, z.p1_stereo70);
      return { ...z, p0_wgs84: a.wgs, p1_wgs84: b.wgs,
               p0_stereo70: a.st, p1_stereo70: b.st };
    });
  }
  return out;
}

/** Punctele WGS rotite cu `grade` (trigonometric, pe hartă: pozitiv = spre
 *  stânga) în jurul lui `centru`, în metri locali. Pentru desenul viu din
 *  hartă, cât se trage de mânerul de rotire; salvarea trece prin
 *  `rotesteConturul`, care rotește metrii. */
export function rotesteWgs(puncte: Pct[], centru: Pct, grade: number): Pct[] {
  if (!puncte.length || !grade) return puncte.slice();
  const { mx, my } = reper(puncte);
  const c = Math.cos(grade * Math.PI / 180), s = Math.sin(grade * Math.PI / 180);
  return puncte.map(([la, lo]) => {
    const x = (lo - centru[1]) * mx, y = (la - centru[0]) * my;
    const xr = x * c - y * s, yr = x * s + y * c;
    return [centru[0] + yr / my, centru[1] + xr / mx] as Pct;
  });
}

/** Centroidul (media vârfurilor) unui contur WGS. */
export function centrulWgs(puncte: Pct[]): Pct {
  return [puncte.reduce((s, p) => s + p[0], 0) / puncte.length,
          puncte.reduce((s, p) => s + p[1], 0) / puncte.length];
}

/** ROTEȘTE conturul cu `grade` în jurul centroidului lui, și zonele cu el.
 *
 *  Pentru lotul fără coordonate Stereo 70 — forma refăcută din lungimile din
 *  act și așezată după planșă sau după adresă —, unde orientarea e o potrivire,
 *  nu o măsurătoare: omul o îndreaptă pe hartă, cum îi mută și poziția
 *  (`mutaConturul`). Aceeași regulă ca acolo: metrii sunt adevărul, gradele se
 *  derivează din ei, ca harta și motorul să vadă același contur.
 *
 *  Sensul e cel trigonometric pe hartă (x est, y nord): pozitiv rotește spre
 *  stânga (antiorar), cum se vede. */
export function rotesteConturul(
  conturWgs: Pct[] | null | undefined,
  conturStereo: Pct[] | null | undefined,
  grade: number,
  zone?: ZonaAcces[] | null,
): { contur_wgs84?: Pct[]; contur_stereo70?: Pct[]; zone_acces?: ZonaAcces[] } {
  if (!grade || (!conturWgs?.length && !conturStereo?.length)) return {};
  const out: { contur_wgs84?: Pct[]; contur_stereo70?: Pct[]; zone_acces?: ZonaAcces[] } = {};
  const c = Math.cos(grade * Math.PI / 180), s = Math.sin(grade * Math.PI / 180);

  if (conturStereo?.length) {
    // Stereo 70: `[nord, est]`; x = est, y = nord.
    const cn = conturStereo.reduce((a, p) => a + p[0], 0) / conturStereo.length;
    const ce = conturStereo.reduce((a, p) => a + p[1], 0) / conturStereo.length;
    const rot = (n: number, e: number): Pct => {
      const x = e - ce, y = n - cn;
      return [cn + x * s + y * c, ce + x * c - y * s];
    };
    const mutat = conturStereo.map(([n, e]) => rot(n, e));
    out.contur_stereo70 = mutat;
    out.contur_wgs84 = mutat.map(([n, e]) => {
      const p = laWgs84(n, e);
      return [p.lat, p.lon] as Pct;
    });
    if (zone?.length) {
      const capat = (w: Pct, st: Pct | null | undefined, centruW: Pct): { wgs: Pct; st: Pct | null } => {
        if (!st) return { wgs: rotesteWgs([w], centruW, grade)[0], st: null };
        const r = rot(st[0], st[1]);
        const p = laWgs84(r[0], r[1]);
        return { wgs: [p.lat, p.lon], st: r };
      };
      const centruW = centrulWgs(conturWgs?.length ? conturWgs : out.contur_wgs84);
      out.zone_acces = zone.map((z) => {
        const a = capat(z.p0_wgs84, z.p0_stereo70, centruW);
        const b = capat(z.p1_wgs84, z.p1_stereo70, centruW);
        return { ...z, p0_wgs84: a.wgs, p1_wgs84: b.wgs, p0_stereo70: a.st, p1_stereo70: b.st };
      });
    }
    return out;
  }

  const centru = centrulWgs(conturWgs!);
  out.contur_wgs84 = rotesteWgs(conturWgs!, centru, grade);
  if (zone?.length) {
    out.zone_acces = zone.map((z) => ({
      ...z,
      p0_wgs84: rotesteWgs([z.p0_wgs84], centru, grade)[0],
      p1_wgs84: rotesteWgs([z.p1_wgs84], centru, grade)[0],
      p0_stereo70: null, p1_stereo70: null,
    }));
  }
  return out;
}

/** O bandă de retragere: până la ce înălțime ține, și cât cere. */
export interface BandaRetragere { h_max_m: number | null; setback_m: number }

/** Retragerea cerută de act la înălțimea dată, sau `null` dacă n-a dat benzi.
 *
 *  Actele scriu retragerea laterală ca o scară pe înălțimea clădirii, nu ca un
 *  număr: certificatul 690/02.07.2025 (UTR M2-CUT3, Sectorul 2), pct. 6 —
 *  «Hmax ≤ 20,0 m → ≥ 5,0 m; > 20,0 și ≤ 30,0 → ≥ 7,5 m; > 30,0 → ≥ 10,0 m».
 *
 *  Aceeași regulă ca în motor (`positioning_plan/backend/envelope.py`), ca
 *  ecranul să nu arate altceva decât se va construi. Pragul e INCLUSIV, fiindcă
 *  actele scriu «≤ 20,0 m», iar banda fără plafon (`h_max_m: null`) e ultima și
 *  prinde tot ce trece peste celelalte — fără ea, o clădire mai înaltă decât
 *  orice prag ar cădea pe regula noastră tocmai unde actul e cel mai strict.
 *
 *  Se sortează AICI, nu se presupune sortat: benzile vin dintr-un act citit de
 *  un model, care le poate da în orice ordine. */
export function retragereDinBanda(
  benzi: BandaRetragere[] | null | undefined, hRef: number | null,
): number | null {
  if (!benzi?.length || hRef == null) return null;
  const ordonate = [...benzi].sort((a, b) =>
    (a.h_max_m == null ? 1 : 0) - (b.h_max_m == null ? 1 : 0)
    || (a.h_max_m ?? 0) - (b.h_max_m ?? 0));
  const gasit = ordonate.find((b) => b.h_max_m == null || hRef <= b.h_max_m);
  return gasit ? gasit.setback_m : null;
}

/** O RETRAGERE PE O PORȚIUNE DE CONTUR, între două puncte desenate în act.
 *
 *  ==========================================================================
 *  CERUT PE 14.10.2026, de la șeful Biancăi: «sa iti recunoasca retrageri
 *  diferite in functie de puncte desenate in documet».
 *
 *  Actele chiar le scriu așa: «între punctele 9-10 retragerea e 6,00 m, pe
 *  restul laturii 3,00 m» — o porțiune de latură, nu o latură întreagă,
 *  fiindcă în spatele ei trece o conductă sau se aliniază la calcanul
 *  vecinului.
 *
 *  Capetele călătoresc în grade ȘI în Stereo 70, iar `latura` e o indicație,
 *  nu un adevăr: consumatorii relipesc capetele pe conturul CURENT, exact ca
 *  la `ZonaAcces` (v. `dosarclient.ts`) — conturul se poate schimba sub ele,
 *  desenat de om sau măsurat din DWG.
 *  ==========================================================================
 */
export interface ZonaRetragere {
  p0_wgs84: Pct;
  p1_wgs84: Pct;
  p0_stereo70?: Pct | null;
  p1_stereo70?: Pct | null;
  /** Cât se cere pe porțiunea asta. */
  setback_m: number;
  /** Indicație despre ce latură a fost, la citire. */
  latura?: number;
  /** Punctul cardinal, când porțiunea vine dintr-o retragere «spre nord…». */
  punct?: string | null;
  /** Fraza din act, pentru om. */
  text?: string | null;
  /** Din ce act vine, sau `"manual"` când a tras-o omul pe hartă.
   *
   *  CONTEAZĂ LA CUMUL: două cerințe din acte se adună ca `max` — amândouă
   *  trebuie respectate —, dar o retragere trasă de mână ÎNLOCUIEȘTE, fiindcă
   *  mâna omului e ultima (aceeași ordine ca în motor, unde un număr scris bate
   *  banda și formula). Fără asta, omul n-ar putea coborî niciodată sub ce cere
   *  actul, deci n-ar putea nici măcar încerca o variantă. */
  sursa: string;
}

/** Conturul tăiat în capetele zonelor, cu o retragere pe fiecare bucată.
 *
 *  ==========================================================================
 *  DE CE AȘA, ȘI NU CU O GEOMETRIE NOUĂ. Cele trei implementări ale
 *  edificabilului — motorul (`geometry.erode_per_side`), ecranul (`zonaEdificabila`)
 *  și validatorul (`adapter.buildable_derived`) — iau O RETRAGERE PE LATURĂ și
 *  trebuie să rămână în paritate. O retragere pe o PORȚIUNE de latură nu cere
 *  însă o a patra regulă: cere doar o latură mai scurtă.
 *
 *  Deci se taie conturul în capetele porțiunilor. Vârfurile adăugate sunt
 *  COLINIARE, deci poligonul e același la milimetru — aceeași arie, aceeași
 *  formă —, numai că acum are câte o latură per porțiune, iar geometria de mai
 *  jos funcționează neatinsă.
 *
 *  NU se taie conturul SALVAT. Inelul din `contur_stereo70` e cel care se
 *  autorizează, iar `laturi[i]` înseamnă latura `i` a LUI în tot ce e în aval
 *  (`siteplan._laturi`, numerele de vârf din acte). Tăietura trăiește numai cât
 *  ține calculul edificabilului.
 *
 *  CÂT DEPARTE AJUNGE, deocamdată: numai până la ECRAN. Porțiunile taie
 *  conturul aici, deci hașura și aria de pe hartă le respectă — dar
 *  `control-plane/services/siteplan.py::_laturi` trimite motorului o retragere
 *  pe latură a conturului NETĂIAT, fiindcă o subdiviziune acolo ar renumerota
 *  laturile, iar `sides[i]`, `zone[].edge` și `PlotEdge.side_index` din
 *  documentul canonic sunt toate legate de numerotarea aceea.
 *
 *  Deci pe un lot cu porțiuni, edificabilul desenat e mai strâns decât cel pe
 *  care îl calculează motorul. Se scrie aici fiindcă paritatea celor trei
 *  implementări e o regulă a casei (v. antetul fișierului), iar o rupere
 *  nescrisă e mai rea decât una știută: cine duce porțiunile mai departe
 *  trebuie să taie și în `siteplan`, cu remaparea indicilor.
 *  ==========================================================================
 *
 *  Zonele care nu cad pe nicio latură se ignoră — un capăt citit greșit dintr-o
 *  schiță n-are voie să mute retragerea pe altă parte a lotului.
 */
export function conturTaiatPeZone(
  contur: Pct[], retrageri: number[], zone: ZonaRetragere[],
  manual: (number | null)[] = [],
  /** Retragerea de bază a laturii `i` e scrisă în act? Dacă nu — e
   *  implicitul nostru, H/2 — o porțiune din act o ÎNLOCUIEȘTE, nu se compară
   *  cu ea. Lipsă = din act. */
  dinAct: boolean[] = [],
): { contur: Pct[]; retrageri: number[]; cerute: number[]; piese: PiesaRetragere[] } {
  // RETRAGERILE TRASE DE MÂNĂ sunt indexate pe conturul ĂSTA, cel nemodificat.
  //
  // Prima formă le ținea ca porțiuni, indexate pe conturul TĂIAT — iar tăierea
  // se schimbă la fiecare salvare, fiindcă adaugă chiar porțiunea tocmai
  // trasă. Deci indicii se mutau sub ele: a doua tragere nimerea altă latură
  // decât cea apucată, iar valorile puse înainte migrau pe vecine. Pe dosarul
  // de pe Șos. Fundeni (15.10.2026) ieșeau cifre împrăștiate — 13,4 / 5,0 /
  // 6,0 / 15,1 — pe laturi pe care nimeni nu le atinsese.
  //
  // Conturul salvat nu se mută decât când omul îl redesenează, și atunci e
  // oricum altă parcelă; e singurul reper stabil pe care se poate indexa.
  const aplicat = contur.map((_, i) => manual[i] ?? retrageri[i] ?? 0);
  if (contur.length < 3 || !zone.length) {
    return { contur, retrageri: aplicat, cerute: retrageri,
             piese: contur.map((_, i) => ({ latura: i, baza: retrageri[i] ?? 0,
                                            bazaDinAct: dinAct[i] ?? true, zone: [],
                                            manual: manual[i] ?? null })) };
  }
  const n = contur.length;
  const { mx, my } = reper(contur);
  const xy = (p: Pct) => [p[1] * mx, p[0] * my] as const;
  // O bucată mai scurtă de atât nu e o latură: capetele porțiunilor vin
  // rotunjite din altă salvare decât conturul și lăsau bucăți de 0,00 m cu
  // retragerea implicită la capete. Aceeași valoare ca în
  // `control-plane/services/siteplan.py::PRAG_BUCATA_M`.
  const PRAG_BUCATA_M = 0.5;
  const lungimea = (i: number) => {
    const [ax, ay] = xy(contur[i]), [bx, by] = xy(contur[(i + 1) % n]);
    return Math.hypot(bx - ax, by - ay);
  };

  /** Parametrul lui `p` pe latura `i`, în [0, 1], și cât de departe cade. */
  const peLatura = (i: number, p: Pct) => {
    const [ax, ay] = xy(contur[i]), [bx, by] = xy(contur[(i + 1) % n]);
    const [px, py] = xy(p);
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0
      : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    return { t, d: Math.hypot(px - (ax + t * dx), py - (ay + t * dy)) };
  };

  // Tăieturile pe fiecare latură: `[t0, t1, retragere]`, cu t în [0, 1].
  const peLaturi: { t0: number; t1: number; d: number; zona: ZonaRetragere }[][] =
    contur.map(() => []);
  for (const z of zone) {
    if (!z.p0_wgs84 || !z.p1_wgs84 || !(z.setback_m >= 0)) continue;
    // Latura e cea pe care cad AMBELE capete cel mai bine — nu cea a
    // mijlocului: o porțiune scurtă lângă un colț are mijlocul la fel de
    // aproape de latura vecină.
    let bun: number | null = null, cost = Infinity;
    for (let i = 0; i < n; i++) {
      const a = peLatura(i, z.p0_wgs84), b = peLatura(i, z.p1_wgs84);
      const c = a.d + b.d;
      if (c < cost) { cost = c; bun = i; }
    }
    if (bun === null) continue;
    const a = peLatura(bun, z.p0_wgs84), b = peLatura(bun, z.p1_wgs84);
    const prag = lungimea(bun) > 0 ? PRAG_BUCATA_M / lungimea(bun) : 1e-6;
    let t0 = Math.min(a.t, b.t), t1 = Math.max(a.t, b.t);
    if (t0 < prag) t0 = 0;
    if (t1 > 1 - prag) t1 = 1;
    if (t1 - t0 < prag) continue;          // porțiune de nimic
    peLaturi[bun].push({ t0, t1, d: z.setback_m, zona: z });
  }

  const conturNou: Pct[] = [];
  const retrageriNoi: number[] = [];
  // CE A INTRAT ÎN FIECARE BUCATĂ, ca ecranul să poată spune de ce cifra e
  // cea care e (tooltip pe cifră, cerut de Oana pe 29.10.2026).
  const piese: PiesaRetragere[] = [];
  // CÂT CER ACTELE pe fiecare bucată, pe lângă cât se aplică. Când omul trage o
  // retragere sub cerință, desenul trebuie să poată arăta FÂȘIA PIERDUTĂ — iar
  // pentru asta nu ajunge valoarea aplicată, trebuie și cea cerută.
  const ceruteNoi: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = contur[i], b = contur[(i + 1) % n];
    const bucati = peLaturi[i];
    if (!bucati.length) {
      conturNou.push(a);
      retrageriNoi.push(aplicat[i]);
      ceruteNoi.push(retrageri[i] ?? 0);
      piese.push({ latura: i, baza: retrageri[i] ?? 0, bazaDinAct: dinAct[i] ?? true,
                   zone: [], manual: manual[i] ?? null });
      continue;
    }
    // Punctele de tăiere, strânse și sortate. 0 și 1 sunt mereu acolo: fără
    // ele, porțiunea care nu începe în vârf ar înghiți capătul laturii.
    const prag = lungimea(i) > 0 ? PRAG_BUCATA_M / lungimea(i) : 1e-6;
    const t = [0];
    for (const x of bucati.flatMap((z) => [z.t0, z.t1]).sort((p, q) => p - q)) {
      if (x - t[t.length - 1] >= prag && 1 - x >= prag) t.push(x);
    }
    t.push(1);
    const laT = (u: number): Pct =>
      [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
    for (let k = 0; k < t.length - 1; k++) {
      const mij = (t[k] + t[k + 1]) / 2;
      // Zona care acoperă mijlocul bucății. Două zone suprapuse: câștigă cea
      // mai MARE retragere — amândouă sunt cerințe, iar cerințele se cumulează
      // ca `max`, la fel ca urbanism vs. pompieri în motor.
      //
      // CU BAZA, `max` numai dacă și ea e scrisă în act. Implicitul nostru
      // (H/2) e ce punem când actul nu spune nimic; când actul spune «spre
      // nord 5,00 m», a spus — iar un maxim cu H/2 = 22,50 ar fi îngropat
      // cifra lui sub presupunerea noastră (Ghencea 91D, 29.10.2026: toate
      // laturile arătau 22,5 peste 5 / 10 / 15 din certificat).
      const baza = retrageri[i] ?? 0;
      const acopera = bucati.filter((z) => mij >= z.t0 && mij <= z.t1);
      piese.push({ latura: i, baza, bazaDinAct: dinAct[i] ?? true,
                   zone: acopera.map((z) => z.zona), manual: manual[i] ?? null });
      const bazaConteaza = !acopera.length || (dinAct[i] ?? true);
      let cerut = bazaConteaza ? baza : 0;
      let d = cerut;
      for (const z of acopera) {
        // Două cerinţe din acte se adună ca `max`: amândouă trebuie respectate.
        cerut = Math.max(cerut, z.d);
        d = Math.max(d, z.d);
      }
      // MÂNA OMULUI E ULTIMA, şi bate şi porţiunile din acte — altfel n-ar
      // putea coborî niciodată sub cerinţă, deci n-ar putea încerca o variantă.
      if (manual[i] != null) d = manual[i] as number;
      conturNou.push(laT(t[k]));
      retrageriNoi.push(d);
      ceruteNoi.push(cerut);
    }
  }
  return { contur: conturNou, retrageri: retrageriNoi, cerute: ceruteNoi, piese };
}

/** O retragere scrisă ca FORMULĂ pe înălțime: «H/3 dar nu mai puțin de 6,0 m».
 *
 *  `raport_h` e cât din H se cere (H/3 → 0,3333), `minim_m` pragul de jos,
 *  `maxim_m` plafonul dacă actul îl dă, iar `text` fraza din act — purtată ca
 *  să i se poată arăta omului REGULA, nu doar cifra ieșită din ea. */
export interface FormulaRetragere {
  raport_h: number | null;
  minim_m: number | null;
  maxim_m: number | null;
  text: string | null;
}

/** Retragerea cerută de formula actului la înălțimea dată, sau `null`.
 *
 *  Aceeași regulă ca în motor (`model.SideSpec.setback_from_formula`) și ca la
 *  import (`dosar/citire.retragere_din_formula`), din același motiv ca benzile:
 *  ecranul nu are voie să arate altceva decât se va construi.
 *
 *  Fără raport nu există formulă, și fără înălțime nu se poate evalua — în
 *  niciunul din cazuri NU se cade pe prag: pragul e plafonul de jos al regulii,
 *  nu regula, iar întors ca răspuns ar trece drept o retragere știută. */
export function retragereDinFormula(
  f: FormulaRetragere | null | undefined, hRef: number | null,
): number | null {
  if (!f || f.raport_h == null || hRef == null) return null;
  let v = f.raport_h * hRef;
  if (f.minim_m != null) v = Math.max(v, f.minim_m);
  if (f.maxim_m != null) v = Math.min(v, f.maxim_m);
  // SE ROTUNJEȘTE LA CENTIMETRU, ca în import (`citire.retragere_din_formula`).
  //
  // Fără asta, un raport citit cu patru zecimale scotea în câmp 10,998 acolo
  // unde actul cere H/3 = 11,00: modelul scrie «0.3333» pentru «H/3», iar
  // 0,3333 × 33 = 10,9989. Omul caută atunci în certificat un «10,998» care nu
  // există nicăieri și nu poate decât să creadă că cifra e inventată.
  //
  // Milimetrul nu există pe o retragere — actele le dau în zeci de centimetri —
  // deci rotunjirea nu pierde nimic și repară și paritatea cu motorul.
  return Math.round(v * 100) / 100;
}

/** Un rând din tabelul «retragere de la aliniament pe categoria străzii». */
export interface CategorieAliniament {
  categorii: string | null;
  retragere_m: number;
}

/** Metrii de folosit din tabel, cât timp nu se știe categoria străzii.
 *
 *  SE IA CEA MAI MARE, adică cea mai strictă. Categoria nu scrie în certificat,
 *  deci între «3 m pentru cat. III-IV» și «5 m pentru cat. I-II» nu se poate
 *  alege pe temei; iar între a construi prea aproape de aliniament și prea
 *  departe, numai prima nu se poate autoriza.
 *
 *  Oricum e mult mai aproape de adevăr decât H/2, care pe un bloc de 33 m dădea
 *  16,50 m — de trei ori mai mult decât cel mai strict rând al actului.
 *  Aceeași regulă ca `citire.retragere_de_la_aliniament`. */
export function retragereDeLaAliniament(
  randuri: CategorieAliniament[] | null | undefined,
): number | null {
  const valori = (randuri ?? [])
    .map((r) => r.retragere_m)
    .filter((x): x is number => x != null);
  return valori.length ? Math.max(...valori) : null;
}

export function zonaEdificabila(contur: Pct[], retrageri: number[]): Pct[][][] {
  if (contur.length < 3) return [];
  const { mx, my } = reper(contur);
  const lot = contur.map((p) => [p[1] * mx, p[0] * my]);
  lot.push(lot[0]);

  const benzi: Polygon[] = [];
  const xy = contur.map((p) => [p[1] * mx, p[0] * my]);
  const capete = capeteleVarfurilor(xy);
  const n = contur.length;
  for (let i = 0; i < n; i++) {
    const d = retrageri[i] ?? 0;
    if (!(d > EPS_M)) continue;
    const a = contur[i], b = contur[(i + 1) % n];
    // Prelungirea la fiecare capăt: exact până la banda vecină (v. `prelungirea`).
    const extA = prelungirea(xy, i, d, retrageri[(i - 1 + n) % n] ?? 0);
    const extB = prelungirea(xy, (i + 1) % n, d, retrageri[(i + 1) % n] ?? 0);
    const inel = banda(a[1] * mx, a[0] * my, b[1] * mx, b[0] * my, d,
                       capete[i], capete[(i + 1) % n], extA, extB);
    if (inel.length) benzi.push([inel]);
  }
  if (!benzi.length) return [[contur.map((p) => [p[0], p[1]] as Pct)]];

  // Nu se înghite nimic aici: dacă tăierea cade, cel care cheamă trebuie să
  // poată SPUNE de ce n-are ce desena. O hașură lipsă fără explicație e cel mai
  // prost rezultat cu putință — omul se uită la un ecran care tace.
  // `taie()` aruncă o eroare cu NUME dacă biblioteca lipsește, în loc să cheme
  // ceva nedefinit — v. `viewer/taietor`.
  const ramas: MultiPolygon = taie().difference([lot as Ring], ...benzi);
  return ramas.map((poly) => poly.map((inel) => {
    const pts = inel.map(([x, y]) => [y / my, x / mx] as Pct);
    // `polygon-clipping` întoarce inele închise; Leaflet le închide singur.
    if (pts.length > 1 && pts[0][0] === pts[pts.length - 1][0]
        && pts[0][1] === pts[pts.length - 1][1]) pts.pop();
    return pts;
  }));
}

/** Aria unui multipoligon [lat, lon], în metri pătrați — găurile scăzute. */
export function ariaZonei(zona: Pct[][][]): number {
  if (!zona.length) return 0;
  const toate = zona.flat().flat();
  if (toate.length < 3) return 0;
  const { mx, my } = reper(toate);
  let total = 0;
  for (const poly of zona) {
    poly.forEach((inel, k) => {
      let s = 0;
      for (let i = 0; i < inel.length; i++) {
        const a = inel[i], b = inel[(i + 1) % inel.length];
        s += (a[1] * mx) * (b[0] * my) - (b[1] * mx) * (a[0] * my);
      }
      total += (k === 0 ? 1 : -1) * Math.abs(s) / 2;
    });
  }
  return total;
}

/** Liniile de hașură ale unei zone, ca segmente [lat, lon].
 *
 *  Scanline-ul e comun cu planul de situație (`viewer/plan/hasura`): acolo se
 *  lucrează în metri de desen, aici în grade, iar singura deosebire e reperul.
 *  O a doua copie a regulii par-impar ar fi însemnat două locuri care se pot
 *  strica separat. */
export function hasura(zona: Pct[][][], pas = 6, unghi = 45): Pct[][] {
  const toate = zona.flat().flat();
  if (toate.length < 3) return [];
  const { mx, my } = reper(toate);
  const inele = zona.flat().map((inel) => inel.map((p) => [p[1] * mx, p[0] * my]));
  return liniiHasuraMulti(inele, pas, unghi)
    .map(([x0, y0, x1, y1]) => [[y0 / my, x0 / mx], [y1 / my, x1 / mx]] as Pct[]);
}

/** E punctul înăuntrul inelului? Ray casting, în grade — destul: inelul e mic
 *  și întrebarea e binară. */
function inauntru(pts: Pct[], p: Pct): boolean {
  let da = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [yi, xi] = pts[i], [yj, xj] = pts[j];
    if ((yi > p[0]) !== (yj > p[0])
        && p[1] < ((xj - xi) * (p[0] - yi)) / ((yj - yi) || 1e-12) + xi) {
      da = !da;
    }
  }
  return da;
}

/** O SECȚIUNE a conturului: o față a lotului, cu retragerea ei.
 *
 *  `laturi` sunt indicii laturilor care o compun, în ordine. */
export interface Sectiune {
  laturi: number[];
  /** Mijlocul feței și normala ei interioară — unde se pune punctul. */
  mijloc: Pct;
  normala: [number, number];
  lungime_m: number;
  setback_m: number;
  cerut_m: number;
}

/** Sub cât o bucată de contur nu e o față, ci un colț al topografului. */
export const SECTIUNE_MIN_M = 10;

/** Conturul, împărțit în FEȚE — nu în laturi.
 *
 *  ==========================================================================
 *  CERUT PE 15.10.2026: «the points should be per section, not micro sections
 *  just normal sections. Basically separate it so that the sections have the
 *  same width».
 *
 *  Un contur de topo are laturi mărunte: cel de pe Șos. Fundeni are 30, dintre
 *  care una de 0,3 m și șase sub 5 m — colțuri măsurate cu grijă, nu fețe ale
 *  lotului. Un punct pe fiecare ar fi un șirag de mărgele, iar o cifră pe
 *  fiecare, desenul de necitit.
 *
 *  DOUĂ TREPTE: se strâng întâi laturile care merg în ACEEAȘI direcție (până la
 *  `COLINIAR_DEG`, pragul folosit și la zonele de acces, și tot pentru că un
 *  front pe care omul îl vede drept vine din topo rupt în bucăți), apoi
 *  bucățile mai scurte de `SECTIUNE_MIN_M` se topesc în vecina mai lungă. Pe
 *  lotul real ies 9 fețe: 12, 28, 84, 22, 17, 73, 36, 126 și 57 m.
 *
 *  FIECARE FAȚĂ ARE O SINGURĂ LĂȚIME, chiar asta s-a cerut: retragerea e a
 *  primei ei laturi, iar cifra se scrie o dată, în banda ei.
 *  ==========================================================================
 */
export function sectiuniContur(
  contur: Pct[], retrageri: number[], cerute: number[] = [],
  pragUnghi = COLINIAR_DEG, pragLung = SECTIUNE_MIN_M,
): Sectiune[] {
  const n = contur.length;
  if (n < 3) return [];
  const { mx, my } = reper(contur);
  const unghi = (i: number) => {
    const a = contur[i], b = contur[(i + 1) % n];
    return Math.atan2((b[0] - a[0]) * my, (b[1] - a[1]) * mx);
  };
  const abatere = (u: number, v: number) => {
    let d = Math.abs(u - v) % (2 * Math.PI);
    if (d > Math.PI) d = 2 * Math.PI - d;
    return (d * 180) / Math.PI;
  };
  const lungLatura = (i: number) => {
    const a = contur[i], b = contur[(i + 1) % n];
    return Math.hypot((b[0] - a[0]) * my, (b[1] - a[1]) * mx);
  };
  const acelasiFel = (i: number, j: number) =>
    Math.abs((retrageri[i] ?? 0) - (retrageri[j] ?? 0)) < 1e-6
    && Math.abs((cerute[i] ?? retrageri[i] ?? 0) - (cerute[j] ?? retrageri[j] ?? 0)) < 1e-6;

  // 1. șiruri de laturi în aceeași direcție ȘI cu aceeași retragere
  let start = 0;
  while (start < n
         && abatere(unghi(start), unghi((start - 1 + n) % n)) <= pragUnghi
         && acelasiFel(start, (start - 1 + n) % n)) start++;
  start %= n;
  let grupuri: number[][] = [];
  let i = 0;
  while (i < n) {
    let k = 1;
    while (k < n - i
           && abatere(unghi((start + i + k) % n), unghi((start + i + k - 1) % n)) <= pragUnghi
           && acelasiFel((start + i + k) % n, (start + i + k - 1) % n)) k++;
    grupuri.push(Array.from({ length: k }, (_, t) => (start + i + t) % n));
    i += k;
  }

  // 2. bucățile prea scurte se topesc în vecina mai lungă — dar numai dacă au
  //    aceeași retragere: o porțiune de 3 m pe care actul cere altceva rămâne,
  //    fiindcă tocmai ea e informația.
  const L = (g: number[]) => g.reduce((s2, j) => s2 + lungLatura(j), 0);
  let schimbat = true;
  while (schimbat && grupuri.length > 3) {
    schimbat = false;
    for (let idx = 0; idx < grupuri.length; idx++) {
      if (L(grupuri[idx]) >= pragLung) continue;
      const a = grupuri[(idx - 1 + grupuri.length) % grupuri.length];
      const b = grupuri[(idx + 1) % grupuri.length];
      const vecini = [a, b].filter((v) => v !== grupuri[idx]
        && acelasiFel(v[0], grupuri[idx][0]));
      if (!vecini.length) continue;
      const tinta = vecini.length === 2
        ? (L(vecini[0]) >= L(vecini[1]) ? vecini[0] : vecini[1]) : vecini[0];
      tinta.push(...grupuri[idx]);
      tinta.sort((u, v) => u - v);
      grupuri.splice(idx, 1);
      schimbat = true;
      break;
    }
  }

  return grupuri.map((g) => {
    const mijl = g[Math.floor(g.length / 2)];
    const a = contur[mijl], b = contur[(mijl + 1) % n];
    return {
      laturi: g,
      mijloc: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as Pct,
      normala: normalaInterioara(contur, mijl),
      lungime_m: L(g),
      setback_m: retrageri[g[0]] ?? 0,
      cerut_m: cerute[g[0]] ?? retrageri[g[0]] ?? 0,
    };
  });
}

/** Cât de aproape trebuie să fie un punct al zonei de retragerea unei fețe ca
 *  să fie al ei: 35 cm. Destul pentru rotunjirile tăierii, prea puțin ca să
 *  încurce două fețe cu retrageri diferite — actele nu dau retrageri la
 *  jumătate de metru una de alta. */
const APARTINE_M = 0.35;

/** PUNCTUL DE PE LINIE al fiecărei fețe — sau `null`, dacă n-are linie.
 *
 *  ==========================================================================
 *  CERUT PE 15.10.2026: «ALL POINTS SHOULD BE PLACED ON THE LINE OF THE
 *  RETRAGERE».
 *
 *  Formele de dinainte CONSTRUIAU punctul: mijlocul feței, dus spre interior cu
 *  retragerea ei. Asta cade pe linie numai acolo unde fața aia singură hotărăște
 *  conturul zonei. Unde o față vecină taie mai adânc, sau unde lotul e concav,
 *  linia adevărată e în altă parte — iar punctul plutea lângă ea. Cu o retragere
 *  mare (pe `tt2` ajunseseră 28 m, din tragerile cu versiunile stricate) el
 *  ieșea de-a dreptul din lot, peste limita opusă.
 *
 *  Se CITEȘTE acum de pe zona desenată, nu se socotește: fiecare vârf al ei se
 *  dă feței de la care stă exact la distanța cerută, iar punctul feței e
 *  mijlocul șirului ei. Pe linie prin construcție, nu prin aritmetică.
 *
 *  FĂRĂ LINIE, FĂRĂ PUNCT. O față a cărei contribuție a fost înghițită de
 *  vecine n-are pe ce sta un mâner, iar unul pus oriunde ar fi o minciună: ea
 *  se scrie în panou, unde cifra se poate tasta.
 *  ==========================================================================
 */
export function liniileRetragerilor(
  contur: Pct[], retrageri: number[], sectiuni: Sectiune[],
): Pct[][] {
  const z = zonaEdificabila(contur, retrageri);
  if (!z.length || !sectiuni.length) return sectiuni.map(() => []);
  const { mx, my } = reper(contur);
  const XY = (p: Pct) => [p[1] * mx, p[0] * my] as [number, number];
  /** Distanța de la un punct la segmentul unei laturi, în metri. */
  const laLatura = (p: [number, number], i: number) => {
    const [ax, ay] = XY(contur[i]);
    const [bx, by] = XY(contur[(i + 1) % contur.length]);
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0
      : Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / len2));
    return Math.hypot(p[0] - (ax + t * dx), p[1] - (ay + t * dy));
  };

  const out: Pct[][] = sectiuni.map(() => []);
  for (let si = 0; si < sectiuni.length; si++) {
    const sec = sectiuni[si];
    const d = retrageri[sec.laturi[0]] ?? 0;
    if (!(d > 0)) continue;
    let bun: Pct[] = [];
    for (const poly of z) {
      const inel = poly[0] ?? [];
      // Vârfurile care stau la FIX retragerea feței, de una din laturile ei.
      const ale: boolean[] = inel.map((p) => {
        const q = XY(p);
        let min = Infinity;
        for (const i of sec.laturi) min = Math.min(min, laLatura(q, i));
        return Math.abs(min - d) <= APARTINE_M;
      });
      // ȘIRURI DE VÂRFURI VECINE PE INEL, nu o mulțime de vârfuri răzlețe.
      //
      // Linia feței e o POLILINIE, iar mânerul alunecă pe ea: ca să alunece,
      // trebuie să existe segmente. Luate ca mulțime, vârfurile dădeau numai
      // puncte, iar mânerul sărea de la unul la altul în loc să urmeze linia —
      // chiar «some of the points still move weird» (15.10.2026).
      //
      // Se taie la întreruperi: două vârfuri care se potrivesc, dar nu sunt
      // vecine pe inel, n-au între ele o latură a zonei, iar coarda dintre ele
      // ar trece prin altceva.
      const n2 = inel.length;
      let start2 = 0;
      while (start2 < n2 && ale[start2] && ale[(start2 - 1 + n2) % n2]) start2++;
      let k = 0;
      while (k < n2) {
        const i0 = (start2 + k) % n2;
        if (!ale[i0]) { k++; continue; }
        const sir: Pct[] = [];
        while (k < n2 && ale[(start2 + k) % n2]) {
          sir.push(inel[(start2 + k) % n2]); k++;
        }
        if (sir.length > bun.length) bun = sir;
      }
    }
    out[si] = bun;
  }
  return out;
}

/** INIMA unui poligon: punctul lui interior cel mai departe de margini.
 *
 *  Unde se scrie numele unui drum. Centrul de greutate nu merge: pe o fâșie
 *  lungă și îndoită cade lipit de margine — pe calea de acces de pe Șos.
 *  Fundeni, MĂSURAT, media vârfurilor e la 0,81 m de margine și centroidul de
 *  arie la 0,43 m, pe o fâșie de 65 m lățime. Tehnic înăuntru, dar plăcuța cu
 *  numele e lată de vreo sută de pixeli, deci călărea limita și se citea ca
 *  fiind pe linia dintre drum și teren («cale de acces should be on the road,
 *  now it's on the dotted line», 16.10.2026). Pe o fâșie îndoită mai tare
 *  centrul iese de tot afară — v. proba cu «L»-ul.
 *
 *  Inima e la 8,7 m de margine pe calea aia.
 *
 *  Se caută pe o grilă care se îndesește: destul pentru o etichetă, și fără
 *  biblioteca de «pole of inaccessibility», care ar fi o dependență nouă pentru
 *  un punct pe care ochiul oricum nu-l măsoară. */
export function inimaPoligonului(inel: Pct[]): Pct | null {
  if (inel.length < 3) return null;
  const { mx, my } = reper(inel);
  const XY = (p: Pct) => [p[1] * mx, p[0] * my] as [number, number];
  const pct = inel.map(XY);
  const xs = pct.map((p) => p[0]), ys = pct.map((p) => p[1]);
  let x0 = Math.min(...xs), x1 = Math.max(...xs);
  let y0 = Math.min(...ys), y1 = Math.max(...ys);
  const inauntruXY = (q: [number, number]) => {
    let da = false;
    for (let i = 0, j = pct.length - 1; i < pct.length; j = i++) {
      const [xi, yi] = pct[i], [xj, yj] = pct[j];
      if ((yi > q[1]) !== (yj > q[1])
          && q[0] < ((xj - xi) * (q[1] - yi)) / ((yj - yi) || 1e-12) + xi) da = !da;
    }
    return da;
  };
  const laMargine = (q: [number, number]) => {
    let min = Infinity;
    for (let i = 0; i < pct.length; i++) {
      const [ax, ay] = pct[i], [bx, by] = pct[(i + 1) % pct.length];
      const dx = bx - ax, dy = by - ay;
      const l2 = dx * dx + dy * dy;
      const t = l2 === 0 ? 0
        : Math.max(0, Math.min(1, ((q[0] - ax) * dx + (q[1] - ay) * dy) / l2));
      min = Math.min(min, Math.hypot(q[0] - (ax + t * dx), q[1] - (ay + t * dy)));
    }
    return min;
  };
  // EGALITĂȚILE SE RUP SPRE MIJLOC. Pe o fâșie de lățime constantă — adică pe
  // un drum drept, cazul cel mai obișnuit — toate punctele de pe axă sunt la
  // fel de adânci, iar un „strict mai mare" păstrează PRIMUL găsit, adică
  // ultimul colț al grilei: numele ajungea lipit de un capăt al drumului, și
  // de acolo textul dădea pe-afară pe la capăt. Între două adâncimi
  // practic egale (sub un milimetru de teren) câștigă cea mai apropiată de
  // mijlocul poligonului.
  const cxm = pct.reduce((a, p) => a + p[0], 0) / pct.length;
  const cym = pct.reduce((a, p) => a + p[1], 0) / pct.length;
  const laMijloc = (q: [number, number]) => Math.hypot(q[0] - cxm, q[1] - cym);
  const EGAL_M = 1e-3;
  let bun: [number, number] | null = null, adanc = -1, mij = Infinity;
  for (let pas = 0; pas < 3; pas++) {
    const n = 24;
    for (let i = 0; i <= n; i++) {
      for (let j = 0; j <= n; j++) {
        const q: [number, number] = [x0 + ((x1 - x0) * i) / n,
                                     y0 + ((y1 - y0) * j) / n];
        if (!inauntruXY(q)) continue;
        const d = laMargine(q);
        const c = laMijloc(q);
        if (d > adanc + EGAL_M || (d > adanc - EGAL_M && c < mij)) {
          adanc = Math.max(adanc, d); mij = c; bun = q;
        }
      }
    }
    if (!bun) return null;
    // Se strânge grila în jurul celui mai bun, ca pasul următor să fie mai fin.
    const r = Math.max((x1 - x0), (y1 - y0)) / 8;
    x0 = bun[0] - r; x1 = bun[0] + r; y0 = bun[1] - r; y1 = bun[1] + r;
  }
  return bun ? [bun[1] / my, bun[0] / mx] : null;
}

/** Cât din lățimea fâșiei ia litera, când are loc. Peste atât, numele nu mai
 *  e o etichetă pe drum, e o firmă. */
export const ETICHETA_PARTE_DIN_BANDA = 0.4;

/** Cât e de lat un rând de text față de înălțimea literei, pe caracter.
 *  Socotit, nu măsurat: un `divIcon` nu e în pagină până nu-l pui pe hartă.
 *  Larg pentru literele noastre, deci greșește spre „nu încape". */
export const ETICHETA_LAT_PE_CARACTER = 0.58;

/** Dreptunghiul `(c, unghi, lung × inalt)` e TOT în poligon?
 *
 *  Colțurile înăuntru NU ajung: pe un poligon concav — și o fâșie de drum e
 *  mai mereu concavă — toate patru pot fi înăuntru în timp ce o scobitură
 *  mușcă din mijlocul laturii. De-aia se cere și ca nicio latură a poligonului
 *  să nu TAIE vreo latură a dreptunghiului. Cele două împreună sunt exacte
 *  pentru un dreptunghi (convex) față de un poligon simplu. */
function cutiaEInauntru(
  pct: [number, number][], c: [number, number],
  unghi: number, lung: number, inalt: number,
): boolean {
  const r = (unghi * Math.PI) / 180, ux = Math.cos(r), uy = Math.sin(r);
  const colt = (sl: number, si: number): [number, number] => [
    c[0] + ((sl * lung) / 2) * ux - ((si * inalt) / 2) * uy,
    c[1] + ((sl * lung) / 2) * uy + ((si * inalt) / 2) * ux,
  ];
  const cutie = [colt(-1, -1), colt(1, -1), colt(1, 1), colt(-1, 1)];
  for (const q of cutie) if (!inauntruXY(pct, q)) return false;
  for (let i = 0; i < 4; i++) {
    const a = cutie[i], b = cutie[(i + 1) % 4];
    for (let k = 0; k < pct.length; k++) {
      if (seTaie(a, b, pct[k], pct[(k + 1) % pct.length])) return false;
    }
  }
  return true;
}

/** Punct în poligon, în reperul metric: aruncare de rază. */
function inauntruXY(pct: [number, number][], q: [number, number]): boolean {
  let da = false;
  for (let i = 0, j = pct.length - 1; i < pct.length; j = i++) {
    const [xi, yi] = pct[i], [xj, yj] = pct[j];
    if ((yi > q[1]) !== (yj > q[1])
        && q[0] < ((xj - xi) * (q[1] - yi)) / ((yj - yi) || 1e-12) + xi) da = !da;
  }
  return da;
}

const semn = (a: [number, number], b: [number, number], c: [number, number]) =>
  Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));

/** Segmentele `a→b` și `c→d` se taie? (cazul propriu, fără coliniarități —
 *  destul aici: o atingere exactă e deja „prea aproape de margine".) */
function seTaie(a: [number, number], b: [number, number],
                c: [number, number], d: [number, number]): boolean {
  return semn(a, b, c) !== semn(a, b, d) && semn(c, d, a) !== semn(c, d, b);
}

/** UNDE, SUB CE UNGHI ȘI CÂT DE MARE se scrie numele unui drum, astfel încât
 *  TOT textul să cadă în el.
 *
 *  ==========================================================================
 *  DE CE NU E DE-AJUNS LĂȚIMEA DIN DREPTUL ETICHETEI. Prima formă lua litera
 *  ca 40 % din lățimea fâșiei la inimă și se oprea acolo. Dar textul e mult
 *  mai LUNG decât e de înalt — «Cale de acces» la 6,9 m înălțime are 52 m
 *  lungime —, iar pe 52 m un drum se îngustează și coteşte. Pe calea de acces
 *  de pe Șos. Fundeni, MĂSURAT, două din patru colțuri ale cutiei de text
 *  cădeau în afara drumului, iar capătul din stânga ieșea de tot («it should
 *  sit inside the road», 16.10.2026). O lățime măsurată într-un punct nu spune
 *  nimic despre ce e la 26 m de el.
 *
 *  CE FACE ÎN LOC. Caută cea mai mare literă pentru care CUTIA ÎNTREAGĂ a
 *  textului încape în poligon, prin înjumătățire; și o caută pe mai multe
 *  direcții, fiindcă unghiul hotărăște cât de mult încape. Rezultatul e o
 *  garanție, nu o presupunere: la mărimea întoarsă, cutia e dovedit înăuntru.
 *
 *  DIRECȚIILE ÎNCERCATE sunt ale laturilor celor mai lungi ale poligonului,
 *  plus axa lui principală. Pe o fâșie, laturile lungi SUNT drumul; axa prinde
 *  cazul în care niciuna nu e limpede. Se ia cea pe care textul iese mai mare.
 *
 *  MĂRIMEA E ÎN METRI, deci la desen se înmulțește cu pixelii pe metru și iese
 *  proporțională cu harta — textul rămâne vopsit pe drum la orice mărire.
 *  Marginea de sus (`ETICHETA_PARTE_DIN_BANDA`) ține numele o etichetă, nu o
 *  firmă, pe un drum foarte lat.
 *
 *  Unghiul se întoarce în (-90°, 90°], ca textul să nu iasă cu capul în jos.
 *  E un unghi de teren, iar Mercator păstrează unghiurile local, deci e și
 *  unghiul de pe ecran (cu `y` întors, fiindcă pe ecran crește în jos). */
export function asezareaEtichetei(inel: Pct[], caractere: number): {
  punct: Pct; unghi_deg: number; inaltime_m: number;
} | null {
  const punct = inimaPoligonului(inel);
  if (!punct || caractere <= 0) return null;
  const { mx, my } = reper(inel);
  const pct = inel.map((p) => [p[1] * mx, p[0] * my] as [number, number]);
  const c: [number, number] = [punct[1] * mx, punct[0] * my];

  // Cât de departe e inima de margine: peste dublul ei nu poate trece nicio
  // cutie centrată acolo, deci e capătul de sus al căutării.
  let adanc = Infinity;
  for (let k = 0; k < pct.length; k++) {
    const [px, py] = pct[k], [qx, qy] = pct[(k + 1) % pct.length];
    const dx = qx - px, dy = qy - py, l2 = dx * dx + dy * dy;
    const t = l2 === 0 ? 0
      : Math.max(0, Math.min(1, ((c[0] - px) * dx + (c[1] - py) * dy) / l2));
    adanc = Math.min(adanc, Math.hypot(c[0] - (px + t * dx), c[1] - (py + t * dy)));
  }
  if (!(adanc > 0)) return null;

  const unghiul = (a: [number, number], b: [number, number]) => {
    let u = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
    u = ((u % 180) + 180) % 180;
    return u > 90 ? u - 180 : u;
  };
  // Laturile cele mai lungi, plus axa principală.
  const laturi = pct.map((p, i) => {
    const q = pct[(i + 1) % pct.length];
    return { u: unghiul(p, q), l: Math.hypot(q[0] - p[0], q[1] - p[1]) };
  }).sort((a, b) => b.l - a.l).slice(0, 6).map((x) => x.u);
  const cx = pct.reduce((s, p) => s + p[0], 0) / pct.length;
  const cy = pct.reduce((s, p) => s + p[1], 0) / pct.length;
  let sxx = 0, syy = 0, sxy = 0;
  for (const p of pct) {
    sxx += (p[0] - cx) ** 2; syy += (p[1] - cy) ** 2;
    sxy += (p[0] - cx) * (p[1] - cy);
  }
  laturi.push(unghiul([0, 0], [Math.cos(0.5 * Math.atan2(2 * sxy, sxx - syy)),
                               Math.sin(0.5 * Math.atan2(2 * sxy, sxx - syy))]));

  // Cutia e puțin mai înaltă decât litera: un rând de text are și ce urcă și
  // ce coboară din el, plus haloul.
  const INALT_RAND = 1.25;
  // Și se probează puțin mai mare decât e: căutarea urcă până CHIAR încape,
  // deci fără marginea asta un colț ajunge exact pe limita drumului — iar
  // haloul alb, care iese din literă, ar călări-o. Pe calea de acces de pe
  // Șos. Fundeni, fără ea un colț ieșea la 0,00 m de margine.
  const MARGINE = 1.08;
  const sus = ETICHETA_PARTE_DIN_BANDA * 2 * adanc;
  let bun: { unghi_deg: number; inaltime_m: number } | null = null;
  for (const u of laturi) {
    let jos = 0, h = sus;
    // Dacă încape de la prima, gata; altfel se înjumătățește.
    const incape = (x: number) => cutiaEInauntru(
      pct, c, u, ETICHETA_LAT_PE_CARACTER * x * caractere * MARGINE,
      x * INALT_RAND * MARGINE);
    if (!incape(h)) {
      let hi = sus;
      for (let i = 0; i < 20; i++) {
        const m = (jos + hi) / 2;
        if (incape(m)) jos = m; else hi = m;
      }
      h = jos;
    }
    if (!bun || h > bun.inaltime_m) bun = { unghi_deg: u, inaltime_m: h };
  }
  return bun && bun.inaltime_m > 0 ? { punct, ...bun } : null;
}

/** Segmentul `a`→`b` trece pe lângă unul dintre poligoane?
 *
 *  Se măsoară de la MIJLOCUL lui: un segment care atinge poligonul doar cu un
 *  capăt îl atinge în colțul unde drumul se termină, și acolo latura pleacă
 *  deja în altă parte.
 */
export function segmentLangaPoligon(
  a: Pct, b: Pct, poligoane: Pct[][], prag_m = 2,
): boolean {
  if (!poligoane.length) return false;
  const { mx, my } = reper([a, b]);
  const mij: [number, number] = [((a[1] + b[1]) / 2) * mx,
                                 ((a[0] + b[0]) / 2) * my];
  for (const inel of poligoane) {
    for (let k = 0; k < inel.length; k++) {
      const p = inel[k], q = inel[(k + 1) % inel.length];
      const px = p[1] * mx, py = p[0] * my;
      const qx = q[1] * mx, qy = q[0] * my;
      const dx = qx - px, dy = qy - py;
      const l2 = dx * dx + dy * dy;
      const t = l2 === 0 ? 0
        : Math.max(0, Math.min(1, ((mij[0] - px) * dx + (mij[1] - py) * dy) / l2));
      if (Math.hypot(mij[0] - (px + t * dx), mij[1] - (py + t * dy)) <= prag_m) {
        return true;
      }
    }
  }
  return false;
}

/** Latura `i` a conturului trece pe lângă unul dintre poligoane? */
export function laturaLangaPoligon(
  contur: Pct[], i: number, poligoane: Pct[][], prag_m = 2,
): boolean {
  if (contur.length < 2 || !poligoane.length) return false;
  return segmentLangaPoligon(contur[i], contur[(i + 1) % contur.length],
                             poligoane, prag_m);
}

/** ANCORA unei fețe: punctul de pe LIMITA ei de sub `p`.
 *
 *  ==========================================================================
 *  DE CE EXISTĂ. Retragerea e o singură mărime — o adâncime —, deci mânerul ei
 *  are voie să se miște pe o singură direcție: perpendicular pe față. Formele
 *  de dinainte îl lăsau să urmeze cursorul ȘI în lungul feței, fiindcă îl
 *  așezau pe punctul de pe linie cel mai apropiat de mână; la o mișcare oblică
 *  aluneca lateral, iar omul nu mai putea nimeri adâncimea pe care o voia —
 *  «sometimes i click on one and it goes side ways instead of vertical»
 *  (15.10.2026).
 *
 *  Ancora se alege o dată, din punctul de pe linie, și rămâne locul feței în
 *  care stă mânerul; de acolo încolo el merge numai înăuntru și înapoi.
 *  ==========================================================================
 */
export function ancoraFetei(contur: Pct[], sec: Sectiune, p: Pct): Pct {
  const { mx, my } = reper(contur);
  let bun: Pct = p, dist = Infinity;
  for (const i of sec.laturi) {
    const a = contur[i], b = contur[(i + 1) % contur.length];
    const ax = a[1] * mx, ay = a[0] * my;
    const bx = b[1] * mx, by = b[0] * my;
    const px = p[1] * mx, py = p[0] * my;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0
      : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const qx = ax + dx * t, qy = ay + dy * t;
    const d = Math.hypot(px - qx, py - qy);
    if (d < dist) { dist = d; bun = [qy / my, qx / mx]; }
  }
  return bun;
}

/** Punctul de pornire al fiecărei fețe: mijlocul liniei ei, sau `null`. */
export function puncteleRetragerilor(
  contur: Pct[], retrageri: number[], sectiuni: Sectiune[],
): (Pct | null)[] {
  return liniileRetragerilor(contur, retrageri, sectiuni)
    .map((linie) => (linie.length ? linie[Math.floor(linie.length / 2)] : null));
}

/** Punctul de pe linie cel mai apropiat de `tinta` — unde se duce mânerul cât
 *  se trage.
 *
 *  ==========================================================================
 *  DE CE NU MIJLOCUL. Prima formă așeza mânerul la mijlocul liniei feței, și la
 *  fiecare cadru al tragerii îl muta acolo. Dar linia se schimbă sub mână, deci
 *  mijlocul ei se plimbă în lungul feței: mânerul fugea de sub deget. Măsurat
 *  pe înregistrarea din 15.10.2026, între două cadre cursorul ajunsese la 80 px
 *  de el — «it's still buggy on certain points when i pull».
 *
 *  Mijlocul rămâne bun ca punct de PORNIRE, când nu există încă un cursor.
 *  ==========================================================================
 */
export function punctulDeSubDeget(linie: Pct[], tinta: Pct): Pct | null {
  if (!linie.length) return null;
  if (linie.length === 1) return linie[0];
  // PE SEGMENTE, nu pe vârfuri: altfel mânerul sare din vârf în vârf, iar
  // linia zonei are puține vârfuri — un salt de zeci de metri între două cadre.
  let bun = linie[0], dist = Infinity;
  for (let i = 0; i < linie.length - 1; i++) {
    const a = linie[i], b = linie[i + 1];
    const dy = b[0] - a[0], dx = b[1] - a[1];
    const len2 = dy * dy + dx * dx;
    const t = len2 === 0 ? 0
      : Math.max(0, Math.min(1, ((tinta[0] - a[0]) * dy
                                 + (tinta[1] - a[1]) * dx) / len2));
    const q: Pct = [a[0] + dy * t, a[1] + dx * t];
    const d = Math.hypot(q[0] - tinta[0], q[1] - tinta[1]);
    if (d < dist) { dist = d; bun = q; }
  }
  return bun;
}

/** ZONA DE PESTE LIMITĂ: ce s-ar construi dincolo de cât permite actul.
 *
 *  Edificabilul cu retragerile APLICATE, minus cel cu retragerile CERUTE. Pe
 *  lotul de pe Șos. Fundeni, trăgând fața de 84 m de la 10,0 la 4,0 m, ies
 *  382 m² — chiar suprafața care nu se poate autoriza.
 *
 *  Se umple galben, nu se hașurează cu linii subțiri: e o suprafață măsurabilă,
 *  iar omul trebuie s-o vadă ca pe o suprafață. */
export function zonaPesteLimita(
  contur: Pct[], retrageri: number[], cerute: number[],
): Pct[][][] {
  if (contur.length < 3) return [];
  const aplicat = zonaEdificabila(contur, retrageri);
  const cerut = zonaEdificabila(contur, cerute);
  if (!aplicat.length) return [];
  if (!cerut.length) return aplicat;
  const { mx, my } = reper(contur);
  const laMetri = (z: Pct[][][]): MultiPolygon =>
    z.map((poly) => poly.map((inel) => inel.map((p) => [p[1] * mx, p[0] * my] as [number, number])));
  const rest = taie().difference(laMetri(aplicat), ...laMetri(cerut));
  return rest.map((poly) =>
    poly.map((inel) => inel.map(([x, y]) => [y / my, x / mx] as Pct)));
}

/** NORMALA INTERIOARĂ a laturii `i`, ca pereche de pași în grade pe metru.
 *
 *  Mutarea cu `d` metri spre interiorul lotului e `lat + nLat*d`, `lon + nLon*d`.
 *
 *  DE CE NU `inafara`. Aia întoarce direcția de la CENTRUL conturului către
 *  mijlocul laturii — bună ca să împingi o etichetă în afară, fiindcă nu
 *  contează pe ce direcție exactă iese. O COTĂ însă trebuie să fie
 *  perpendiculară pe limită, altfel nu măsoară retragerea, ci o oblică mai
 *  lungă decât ea; pe un lot alungit, cum e cel de pe Șos. Fundeni, abaterea se
 *  vede cu ochiul liber și cotele ies trase strâmb peste parcelă.
 *
 *  Sensul se alege către centru, nu din orientarea inelului: un contur desenat
 *  de om poate veni în orice sens, iar un semn greșit ar trage cota în afara
 *  lotului. */
export function normalaInterioara(pts: Pct[], i: number): [number, number] {
  const { mx, my } = reper(pts);
  const a = pts[i], b = pts[(i + 1) % pts.length];
  const ax = a[1] * mx, ay = a[0] * my;
  const bx = b[1] * mx, by = b[0] * my;
  const ex = bx - ax, ey = by - ay;
  const len = Math.hypot(ex, ey) || 1;
  // Perpendiculara pe latură, în metri.
  let nx = -ey / len, ny = ex / len;
  // Spre interior: se află INTRÂND, nu arătând spre centru — pe un contur
  // concav centrul poate cădea dincolo de o față (v. `normalaCoardei`).
  const mijLat = (a[0] + b[0]) / 2, mijLon = (a[1] + b[1]) / 2;
  const pas = 0.2;
  const p: Pct = [mijLat + (ny / my) * pas, mijLon + (nx / mx) * pas];
  if (!inauntru(pts, p)) { nx = -nx; ny = -ny; }
  return [ny / my, nx / mx];
}

/** FÂȘIILE PIERDUTE: ce se construiește peste cât cere actul.
 *
 *  Pe fiecare latură unde retragerea aplicată e mai mică decât cea cerută,
 *  întoarce fâșia dintre ele — banda de la retragerea trasă de om până la cea
 *  pe care o cere certificatul. Aia e, la metru pătrat, suprafața care nu se
 *  poate autoriza.
 *
 *  Se desenează galben, nu roșu: roșul e «aici nu se construiește», iar ăsta e
 *  un alt fel de lucru — «aici ai cerut să construiești, dar actul nu te lasă».
 *  Un avertisment, nu o interdicție, fiindcă omul a ales-o cu mâna lui și are
 *  voie să încerce o variantă înainte s-o ceară la primărie. */
export function fasiiPierdute(
  contur: Pct[], retrageri: number[], cerute: number[],
): Pct[][][] {
  if (contur.length < 3) return [];
  const { mx, my } = reper(contur);
  const lot = contur.map((p) => [p[1] * mx, p[0] * my]);
  lot.push(lot[0]);
  const bucati: Polygon[] = [];
  for (let i = 0; i < contur.length; i++) {
    const aplicat = retrageri[i] ?? 0;
    const cerut = cerute[i] ?? 0;
    if (!(cerut > aplicat + EPS_M)) continue;
    const a = contur[i], b = contur[(i + 1) % contur.length];
    const mare = banda(a[1] * mx, a[0] * my, b[1] * mx, b[0] * my, cerut);
    if (!mare.length) continue;
    let fasie: MultiPolygon = [[mare]];
    if (aplicat > EPS_M) {
      const mic = banda(a[1] * mx, a[0] * my, b[1] * mx, b[0] * my, aplicat);
      if (mic.length) fasie = taie().difference(fasie, [mic]);
    }
    // Numai partea dinăuntrul lotului: banda unui segment iese și în afara lui.
    const inlot = taie().intersection(fasie, [lot as Ring]);
    for (const poly of inlot) bucati.push(poly);
  }
  return bucati.map((poly) =>
    poly.map((inel) => inel.map(([x, y]) => [y / my, x / mx] as Pct)));
}

/** Rezultatul întreg: zona, aria ei, și motivul când nu e nimic de arătat. */
/** Ce a intrat într-o bucată de latură a conturului tăiat: baza (cifra
 *  generală a felului laturii) şi dacă e scrisă în act, porţiunile din act care
 *  o acoperă, şi ce a tras omul pe latura de origine. */
export interface PiesaRetragere {
  latura: number;
  baza: number;
  bazaDinAct: boolean;
  zone: ZonaRetragere[];
  manual: number | null;
}

export interface Zona {
  zona: Pct[][][];
  arie: number;
  /** Scris pentru om, `null` când e totul în regulă. */
  motiv: string | null;
  /** CONTURUL PE CARE S-AU APLICAT RETRAGERILE și retragerea fiecărei laturi a
   *  lui, în aceeași ordine.
   *
   *  E conturul TĂIAT pe porțiuni (v. `conturTaiatPeZone`), nu cel salvat: pe
   *  el o latură înseamnă o retragere, deci din perechea asta se pot scrie
   *  cotele pe desen. Poligonul e același — vârfurile adăugate sunt coliniare
   *  —, deci cotele cad pe linia pe care o vede omul. */
  contur: Pct[];
  retrageri: number[];
  piese: PiesaRetragere[];
  /** CÂT CER ACTELE pe fiecare latură, pe lângă cât se aplică.
   *
   *  Diferă de `retrageri` numai acolo unde omul a tras o retragere sub
   *  cerință; din perechea asta se desenează fâșia pierdută, hașurată galben. */
  cerute: number[];
  /** Retragerea APLICATĂ și cea CERUTĂ pe fiecare latură a conturului SALVAT.
   *
   *  `contur`/`retrageri` de mai sus sunt pe conturul tăiat pe porțiuni — bun
   *  pentru geometrie, dar indicii lui se mută. Astea două sunt pe inelul pe
   *  care îl vede omul și pe care stau mânerele, deci pe ele se fac secțiunile
   *  și cifrele. */
  peLatura: { aplicat: number[]; cerut: number[] };
}

/** Zona edificabilă a unei parcele, cu tot cu felul laturilor.
 *
 *  Un singur loc care știe tot drumul — de la ce scrie în ecran până la
 *  poligonul desenat — ca să se poată încerca fără browser. Și, mai ales, un
 *  singur loc care răspunde la «de ce nu văd nimic»: fiecare cale care nu duce
 *  la o zonă își spune motivul, în loc să întoarcă o listă goală. */
export function calculeazaZona(
  contur: Pct[] | null | undefined, laturi: Latura[], zone: ZonaAcces[],
  strada: number | null, vecini: number | null,
  retrageriPeZone: ZonaRetragere[] = [],
  /** Retragerile trase de mână, câte una per latură a conturului SALVAT. */
  retrageriManual: (number | null)[] = [],
  /** Cifrele generale (`strada`, `vecini`) sunt scrise în act, sau sunt
   *  implicitul nostru (H/2)? De asta depinde cum se combină cu o porțiune
   *  din act (v. `conturTaiatPeZone`). Lipsă = din act. */
  bazaDinAct: { strada: boolean; vecini: boolean } = { strada: true, vecini: true },
): Zona {
  const gol = (motiv: string): Zona =>
    ({ zona: [], arie: 0, motiv, contur: [], retrageri: [], cerute: [], piese: [],
       peLatura: { aplicat: [], cerut: [] } });
  if (!contur || contur.length < 3) return gol(t("edificabil.fara_contur"));
  if (strada == null && vecini == null && !retrageriPeZone.length)
    return gol(t("edificabil.fara_retrageri"));
  const fel = felurileLaturilor(contur, laturi, zone);
  const deBaza = fel.map((f) => (f === "strada" ? strada : vecini) ?? 0);
  const dinAct = fel.map((f) => (f === "strada" ? bazaDinAct.strada : bazaDinAct.vecini));
  // PORȚIUNILE DESENATE ÎN ACT taie conturul în laturi mai scurte, câte una
  // per porțiune (v. `conturTaiatPeZone`). Vârfurile adăugate sunt coliniare,
  // deci poligonul rămâne același — se schimbă doar câte retrageri are de
  // aplicat geometria de dedesubt, care nu se atinge.
  const taiat = conturTaiatPeZone(contur, deBaza, retrageriPeZone,
                                  retrageriManual, dinAct);
  const retrageri = taiat.retrageri;
  if (!retrageri.some((d) => d > 0))
    return gol(t("edificabil.retrageri_zero"));
  let zona: Pct[][][];
  try {
    zona = zonaEdificabila(taiat.contur, retrageri);
  } catch (e) {
    return gol(`geometria n-a putut fi tăiată: ${String(e).slice(0, 70)}`);
  }
  if (!zona.length) return gol(t("edificabil.acoperit"));
  return { zona, arie: ariaZonei(zona), motiv: null,
           contur: taiat.contur, retrageri, cerute: taiat.cerute, piese: taiat.piese,
           peLatura: {
             aplicat: contur.map((_, i) => retrageriManual[i] ?? deBaza[i] ?? 0),
             cerut: contur.map((_, i) => deBaza[i] ?? 0),
           } };
}
